import { useEffect, useRef, useState, useCallback } from 'react';
import { hsvToRgb, rgbToHsv, rgbToHex, hexToRgb } from '../utils/color';
import { useTranslation } from 'react-i18next';

/**
 * A colour picker with:
 *  - Square SV (saturation/value) field with hue bar below
 *  - RGB number inputs
 *  - Hex paste/type input
 *
 * Props:
 *   value: string  — current hex colour (e.g. '#7c3aed')
 *   onChange: (hex: string) => void
 */
export default function ColorPicker({ value, onChange }) {
  const { t } = useTranslation();
  const svCanvasRef  = useRef(null);
  const hueCanvasRef = useRef(null);
  const svDragging   = useRef(false);
  const hueDragging  = useRef(false);
  const eyeDropperSupported = typeof window !== 'undefined' && 'EyeDropper' in window;

  // Parse incoming value to HSV; default to purple
  const initHsv = () => {
    const rgb = hexToRgb(value);
    return rgb ? rgbToHsv(rgb) : { h: 270, s: 0.8, v: 0.7 };
  };

  const [hsv, setHsv] = useState(initHsv);
  const [hexInput, setHexInput] = useState(value || '#7c3aed');
  const [rgbInputs, setRgbInputs] = useState(() => {
    const rgb = hexToRgb(value) || { r: 124, g: 58, b: 237 };
    return { r: String(rgb.r), g: String(rgb.g), b: String(rgb.b) };
  });

  // Sync outward when hsv changes (but not when we received a new `value` prop)
  const isExternalUpdate = useRef(false);

  useEffect(() => {
    if (isExternalUpdate.current) {
      isExternalUpdate.current = false;
      return;
    }
    const rgb = hsvToRgb(hsv.h, hsv.s, hsv.v);
    const hex = rgbToHex(rgb);
    setHexInput(hex);
    setRgbInputs({ r: String(rgb.r), g: String(rgb.g), b: String(rgb.b) });
    onChange(hex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hsv]);

  // Sync inward when value prop changes from outside
  useEffect(() => {
    const rgb = hexToRgb(value);
    if (!rgb) return;
    const newHex = rgbToHex(rgb);
    if (newHex === hexInput) return;
    isExternalUpdate.current = true;
    const newHsv = rgbToHsv(rgb);
    setHsv(newHsv);
    setHexInput(newHex);
    setRgbInputs({ r: String(rgb.r), g: String(rgb.g), b: String(rgb.b) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Draw SV square
  const drawSv = useCallback(() => {
    const canvas = svCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;

    // White → pure-hue horizontal gradient
    const hueRgb = hsvToRgb(hsv.h, 1, 1);
    const hueStr = `rgb(${hueRgb.r},${hueRgb.g},${hueRgb.b})`;
    const gradH = ctx.createLinearGradient(0, 0, w, 0);
    gradH.addColorStop(0, '#fff');
    gradH.addColorStop(1, hueStr);
    ctx.fillStyle = gradH;
    ctx.fillRect(0, 0, w, h);

    // Transparent → black vertical gradient
    const gradV = ctx.createLinearGradient(0, 0, 0, h);
    gradV.addColorStop(0, 'rgba(0,0,0,0)');
    gradV.addColorStop(1, 'rgba(0,0,0,1)');
    ctx.fillStyle = gradV;
    ctx.fillRect(0, 0, w, h);

    // Crosshair
    const cx = hsv.s * w;
    const cy = (1 - hsv.v) * h;
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(cx, cy, 7, 0, Math.PI * 2);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(0,0,0,0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, 7, 0, Math.PI * 2);
    ctx.stroke();
  }, [hsv.h, hsv.s, hsv.v]);

  // Draw hue bar
  const drawHue = useCallback(() => {
    const canvas = hueCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    const grad = ctx.createLinearGradient(0, 0, w, 0);
    for (let i = 0; i <= 6; i++) {
      const rgb = hsvToRgb(i * 60, 1, 1);
      grad.addColorStop(i / 6, `rgb(${rgb.r},${rgb.g},${rgb.b})`);
    }
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    // Hue cursor
    const x = (hsv.h / 360) * w;
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.strokeRect(x - 5, 1, 10, h - 2);
    ctx.strokeStyle = 'rgba(0,0,0,0.5)';
    ctx.lineWidth = 1;
    ctx.strokeRect(x - 5, 1, 10, h - 2);
  }, [hsv.h]);

  useEffect(() => { drawSv(); }, [drawSv]);
  useEffect(() => { drawHue(); }, [drawHue]);

  function svFromEvent(e, canvas) {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const s = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const v = Math.min(1, Math.max(0, 1 - (clientY - rect.top) / rect.height));
    return { s, v };
  }

  function hueFromEvent(e, canvas) {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    return Math.min(360, Math.max(0, ((clientX - rect.left) / rect.width) * 360));
  }

  // SV pointer handlers
  function onSvPointerDown(e) {
    svDragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    const { s, v } = svFromEvent(e, e.currentTarget);
    setHsv((prev) => ({ ...prev, s, v }));
  }
  function onSvPointerMove(e) {
    if (!svDragging.current) return;
    const { s, v } = svFromEvent(e, e.currentTarget);
    setHsv((prev) => ({ ...prev, s, v }));
  }
  function onSvPointerUp() { svDragging.current = false; }

  // Hue pointer handlers
  function onHuePointerDown(e) {
    hueDragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    const h = hueFromEvent(e, e.currentTarget);
    setHsv((prev) => ({ ...prev, h }));
  }
  function onHuePointerMove(e) {
    if (!hueDragging.current) return;
    const h = hueFromEvent(e, e.currentTarget);
    setHsv((prev) => ({ ...prev, h }));
  }
  function onHuePointerUp() { hueDragging.current = false; }

  // Hex input
  function onHexChange(e) {
    const raw = e.target.value;
    setHexInput(raw);
    const cleaned = raw.startsWith('#') ? raw : '#' + raw;
    const rgb = hexToRgb(cleaned);
    if (rgb) {
      setHsv(rgbToHsv(rgb));
      setRgbInputs({ r: String(rgb.r), g: String(rgb.g), b: String(rgb.b) });
      onChange(rgbToHex(rgb));
    }
  }

  // RGB inputs
  function onRgbChange(channel, rawVal) {
    setRgbInputs((prev) => ({ ...prev, [channel]: rawVal }));
    const n = parseInt(rawVal, 10);
    if (!isNaN(n) && n >= 0 && n <= 255) {
      const current = hsvToRgb(hsv.h, hsv.s, hsv.v);
      const updated = { ...current, [channel]: n };
      const newHsv = rgbToHsv(updated);
      const newHex = rgbToHex(updated);
      setHsv(newHsv);
      setHexInput(newHex);
      onChange(newHex);
    }
  }

  async function handleEyeDropper() {
    try {
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      const rgb = hexToRgb(result.sRGBHex);
      if (rgb) setHsv(rgbToHsv(rgb)); // triggers the onChange effect
    } catch {
      // User cancelled or permission denied — no action needed
    }
  }

  const previewColor = rgbToHex(hsvToRgb(hsv.h, hsv.s, hsv.v));

  return (
    <div className="space-y-3 select-none">
      {/* SV field */}
      <canvas
        ref={svCanvasRef}
        width={220}
        height={160}
        className="w-full rounded-lg cursor-crosshair touch-none"
        style={{ display: 'block' }}
        onPointerDown={onSvPointerDown}
        onPointerMove={onSvPointerMove}
        onPointerUp={onSvPointerUp}
        aria-label="Colour field — drag to set saturation and brightness"
      />

      {/* Hue bar */}
      <canvas
        ref={hueCanvasRef}
        width={220}
        height={18}
        className="w-full rounded cursor-ew-resize touch-none"
        style={{ display: 'block' }}
        onPointerDown={onHuePointerDown}
        onPointerMove={onHuePointerMove}
        onPointerUp={onHuePointerUp}
        aria-label="Hue bar — drag to change hue"
      />

      {/* Preview swatch + hex input + eyedropper */}
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded border t-border-input shrink-0"
          style={{ backgroundColor: previewColor }}
          aria-hidden="true"
        />
        <input
          type="text"
          value={hexInput}
          onChange={onHexChange}
          maxLength={7}
          spellCheck={false}
          className="flex-1 t-bg-input t-border-input border rounded px-2 py-1.5 text-sm font-mono t-text t-focus-input"
          aria-label="Hex colour code"
        />
        {eyeDropperSupported && (
          <button
            type="button"
            onClick={handleEyeDropper}
            className="w-8 h-8 flex items-center justify-center rounded t-bg-input t-border-input border t-text-muted hover:t-text transition-colors t-focus-ring shrink-0"
            aria-label={t('customTheme.eyeDropper')}
            title={t('customTheme.eyeDropper')}
          >
            {/* Eyedropper icon */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden="true">
              <path fillRule="evenodd" d="M3.28 2.22a.75.75 0 0 0-1.06 1.06l.74.74-.02.02a3 3 0 0 0 0 4.24l5.5 5.5a3 3 0 0 0 4.24 0l.02-.02.74.74a.75.75 0 1 0 1.06-1.06l-.74-.74.02-.02a3 3 0 0 0 0-4.24l-5.5-5.5a3 3 0 0 0-4.24 0l-.02.02-.74-.74ZM4.5 6a1.5 1.5 0 0 1 2.121-1.378L11.5 9.5l-1.5 1.5L5.121 6.121A1.5 1.5 0 0 1 4.5 6ZM14.78 9.28a.75.75 0 0 1 0 1.06l-2.5 2.5a.75.75 0 0 1-1.06-1.06l2.5-2.5a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>

      {/* RGB inputs */}
      <div className="flex gap-2">
        {['r', 'g', 'b'].map((ch) => (
          <label key={ch} className="flex-1 flex flex-col gap-0.5">
            <span className="text-xs t-text-muted uppercase text-center">{ch}</span>
            <input
              type="number"
              min={0}
              max={255}
              value={rgbInputs[ch]}
              onChange={(e) => onRgbChange(ch, e.target.value)}
              className="w-full t-bg-input t-border-input border rounded px-2 py-1.5 text-sm text-center t-text t-focus-input"
              aria-label={`${ch.toUpperCase()} value`}
            />
          </label>
        ))}
      </div>
    </div>
  );
}
